import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import TaskModal from '../../components/navigation/TaskModal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import FormField from './components/FormField';
import TimeEstimateInput from './components/TimeEstimateInput';
import TagInput from './components/TagInput';
import QuadrantSelector from './components/QuadrantSelector';

const TaskCreationEditModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editTask = location?.state?.task;
  const isEditMode = !!editTask;

  const [formData, setFormData] = useState({
    title: '',
    notes: '',
    dueDate: '',
    dueTime: '',
    estimatedTime: 30,
    tags: [],
    urgency: false,
    importance: false,
    repeat: 'none'
  });

  const [errors, setErrors] = useState({});
  const [showSuccessToast, setShowSuccessToast] = useState(false);

  useEffect(() => {
    if (editTask) {
      setFormData({
        title: editTask?.title || '',
        notes: editTask?.notes || '',
        dueDate: editTask?.dueDate || '',
        dueTime: editTask?.dueTime || '',
        estimatedTime: editTask?.estimatedTime || 30,
        tags: editTask?.tags || [],
        urgency: editTask?.urgency || false,
        importance: editTask?.importance || false,
        repeat: editTask?.repeat || 'none'
      });
    }
  }, [editTask]);

  const tagSuggestions = [
    'Work',
    'Personal',
    'Health',
    'Finance',
    'Learning',
    'Shopping',
    'Home',
    'Family',
    'Project',
    'Meeting',
    'Call',
    'Email',
    'Review',
    'Planning'
  ];

  const repeatOptions = [
    { value: 'none', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekdays', label: 'Every weekday (Mon-Fri)' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
    { value: 'custom', label: 'Custom...' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Task title is required';
    } else if (formData?.title?.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    if (formData?.notes?.length > 500) {
      newErrors.notes = 'Notes must be less than 500 characters';
    }

    if (formData?.dueDate) {
      const selectedDate = new Date(formData.dueDate);
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.dueDate = 'Due date cannot be in the past';
      }
    }

    if (formData?.tags?.length > 10) {
      newErrors.tags = 'Maximum 10 tags allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    const taskData = {
      ...formData,
      id: editTask?.id || Date.now(),
      createdAt: editTask?.createdAt || new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };

    console.log(isEditMode ? 'Updating task:' : 'Creating task:', taskData);

    setShowSuccessToast(true);
    setTimeout(() => {
      navigate('/main-dashboard', { 
        state: { 
          task: taskData, 
          action: isEditMode ? 'updated' : 'created' 
        } 
      });
    }, 1000);
  };

  const handleClose = () => {
    navigate('/main-dashboard');
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <TaskModal
          isOpen={true}
          onClose={handleClose}
          onSave={handleSave}
          title={isEditMode ? 'Edit Task' : 'Create New Task'}
        >
          <div className="space-y-4 md:space-y-5 lg:space-y-6">
            <FormField
              label="Task Title"
              required
              error={errors?.title}
              description="Brief description of what needs to be done"
            >
              <Input
                type="text"
                placeholder="e.g., Complete project proposal"
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                error={errors?.title}
                required
              />
            </FormField>

            <FormField
              label="Notes"
              error={errors?.notes}
              description="Additional details, context, or instructions"
            >
              <textarea
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                placeholder="Add any additional information about this task..."
                rows={4}
                className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg bg-input border border-border text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-muted-foreground">
                  {formData?.notes?.length}/500
                </span>
              </div>
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              <FormField
                label="Due Date"
                error={errors?.dueDate}
                required={false}
                description=""
              >
                <Input
                  type="date"
                  value={formData?.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e?.target?.value)}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />
              </FormField>

              <FormField
                label="Due Time"
                required={false}
                error=""
                description=""
              >
                <Input
                  type="time"
                  value={formData?.dueTime}
                  onChange={(e) => handleInputChange('dueTime', e?.target?.value)}
                />
              </FormField>
            </div>

            <FormField
              label="Estimated Time"
              description="How long do you think this task will take?"
              required={false}
              error=""
            >
              <TimeEstimateInput
                value={formData?.estimatedTime}
                onChange={(value) => handleInputChange('estimatedTime', value)}
                error=""
              />
            </FormField>

            <FormField
              label="Tags"
              error={errors?.tags}
              description="Add tags to categorize and find tasks easily"
            >
              <TagInput
                value={formData?.tags}
                onChange={(value) => handleInputChange('tags', value)}
                suggestions={tagSuggestions}
                error={errors?.tags}
              />
            </FormField>

            <FormField
              label="Priority Quadrant"
              description="Select urgency and importance to automatically assign quadrant"
            >
              <QuadrantSelector
                urgency={formData?.urgency}
                importance={formData?.importance}
                onUrgencyChange={(value) => handleInputChange('urgency', value)}
                onImportanceChange={(value) => handleInputChange('importance', value)}
              />
            </FormField>

            <FormField
              label="Repeat"
              description="Set up recurring tasks"
            >
              <Select
                options={repeatOptions}
                value={formData?.repeat}
                onChange={(value) => handleInputChange('repeat', value)}
                placeholder="Select repeat frequency"
              />
            </FormField>
          </div>
        </TaskModal>
      </div>
      {showSuccessToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1001] animate-slideUp">
          <div className="bg-success text-success-foreground px-4 md:px-6 py-3 md:py-4 rounded-lg shadow-soft-xl flex items-center gap-3">
            <div className="w-5 h-5 rounded-full bg-success-foreground/20 flex items-center justify-center">
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 3L4.5 8.5L2 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="text-sm md:text-base font-medium">
              Task {isEditMode ? 'updated' : 'created'} successfully!
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCreationEditModal;